import { Controller, useFormContext } from 'react-hook-form'
import { CurecallMultiSelect } from '@honestica/curecall-ui'
import { useTranslation } from 'react-i18next'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { useMemo } from 'react'
import { TFollowup } from 'src/types/followup'
import { PlanningTabFormValues } from './../planning.type'
import { getDoctorName } from 'src/utils/string-formatter.util'
import { TNavigationLocales } from 'src/types/locales'

export function PlanningTabFilterFields() {
  const { t, i18n } = useTranslation()
  const locale = i18n.language as TNavigationLocales
  const {
    control,
    formState: { errors },
  } = useFormContext<PlanningTabFormValues>()
  const { followups, availableMedEventOptions, activeDoctors, locations = [] } = useCurrentProject()

  const LOCATION_OPTIONS = useMemo(
    () =>
      locations
        .map((location) => ({ label: location.name, value: location.id }))
        .sort((a, b) => a.label.localeCompare(b.label, locale, { sensitivity: 'base' })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [locations],
  )

  const FOLLOWUP_OPTIONS = useMemo(
    () =>
      followups
        ?.map((followup: TFollowup) => ({ label: followup.name, value: followup.id }))
        .sort((a, b) => a.label.localeCompare(b.label, locale, { sensitivity: 'base' })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [followups],
  )

  const noMedicalStaffMemberOption = { label: t('pages.planning.filters.not-specified'), value: null }
  const MEDICAL_STAFF_MEMBER_OPTIONS = useMemo(
    () => [
      noMedicalStaffMemberOption,
      ...activeDoctors
        .slice()
        .sort((a, b) => {
          const nameA = a.lastname + ' ' + a.firstname
          const nameB = b.lastname + ' ' + b.firstname
          return nameA.localeCompare(nameB, locale, { sensitivity: 'base' })
        })
        .map((medicalStaffMember) => ({
          label: getDoctorName(medicalStaffMember),
          value: medicalStaffMember.id,
        })),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeDoctors],
  )

  return (
    <div className="c-flex c-gap-6">
      <Controller
        name="followupIdsFilter"
        control={control}
        render={({ field }) => (
          <CurecallMultiSelect
            label={t('pages.planning.form.select-followups')}
            tooltipContent={t('pages.planning.form.select-followups-tooltip')}
            tooltipPosition="bottom"
            items={FOLLOWUP_OPTIONS}
            selectedItems={FOLLOWUP_OPTIONS.filter((option) => field.value?.includes(option.value))}
            onSelect={(options) => field.onChange(options.map((option) => option.value))}
            selectedElementsLabel={t('pages.planning.form.selected', { number: field.value?.length })}
            errorMessage={errors['followupIdsFilter']?.message}
            wrapperClassName="c-w-72"
            optionsClassName="tw-w-72"
          />
        )}
      />
      <Controller
        name="medEventIdsFilter"
        control={control}
        render={({ field }) => (
          <CurecallMultiSelect
            label={t('pages.planning.form.select-med-events')}
            tooltipContent={t('pages.planning.form.select-med-events-tooltip')}
            tooltipPosition="bottom"
            items={availableMedEventOptions}
            selectedItems={availableMedEventOptions.filter((option) => field.value?.includes(option.value))}
            onSelect={(options) => field.onChange(options.map((option) => option.value))}
            selectedElementsLabel={t('pages.planning.form.selected', { number: field.value?.length })}
            errorMessage={errors['medEventIdsFilter']?.message}
            wrapperClassName="c-w-72"
            optionsClassName="tw-w-72"
          />
        )}
      />
      <Controller
        name="medicalStaffMemberIdsFilter"
        control={control}
        render={({ field }) => (
          <CurecallMultiSelect
            label={t('pages.planning.form.medical-staff-members')}
            tooltipContent={t('pages.planning.form.select-medical-staff-members-tooltip')}
            tooltipPosition="bottom"
            items={MEDICAL_STAFF_MEMBER_OPTIONS}
            selectedItems={MEDICAL_STAFF_MEMBER_OPTIONS.filter((option) =>
              field.value?.includes(option.value),
            )}
            onSelect={(options) => field.onChange(options.map((option) => option.value))}
            selectedElementsLabel={t('pages.planning.form.selected', { number: field.value?.length })}
            isSearchable={true}
            searchPlaceholder={t('pages.planning.filters.medical-staff-member-search-placeholder')}
            noResultText={t('pages.planning.filters.medical-staff-member-no-result')}
            errorMessage={errors['medicalStaffMemberIdsFilter']?.message}
            wrapperClassName="c-w-72"
            optionsClassName="tw-w-72"
          />
        )}
      />
      {locations.length > 1 && (
        <Controller
          name="locationIdsFilter"
          control={control}
          render={({ field }) => (
            <CurecallMultiSelect
              label={t('pages.planning.form.select-locations')}
              tooltipContent={t('pages.planning.form.select-locations-tooltip')}
              tooltipPosition="bottom-end"
              items={LOCATION_OPTIONS}
              selectedItems={LOCATION_OPTIONS.filter((option) => field.value?.includes(option.value))}
              onSelect={(options) => field.onChange(options.map((option) => option.value))}
              selectedElementsLabel={t('pages.planning.form.selected', { number: field.value?.length })}
              errorMessage={errors['locationIdsFilter']?.message}
              wrapperClassName="c-w-72"
              optionsClassName="tw-w-72"
            />
          )}
        />
      )}
    </div>
  )
}
